/**
 * @prettier
 * @author Alexander Nagelberg
 *
 **/
import React, { useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import DatePicker from 'react-datepicker'

import { v4 as uuid } from 'uuid'

import LgyAlertDialog from 'app/components/util/lgy-alert-dialog/LgyAlertDialog'

import 'react-datepicker/dist/react-datepicker.css'
import '../style/fundingfee.scss'

/**
 * Updates the URL with ?from=epoch&to=epoch
 *
 * @param {Object} prop
 * @param {boolean} prop.automatic - Whether to apply date automatically on change or user intervention
 *
 * @example
 * <DateRangePicker automatic /> displays component with from: and to: date pickers that changes URL as
 * dates are picked.
 *
 * <DateRangePicker /> displays component with from: and to: date pickers and a Submit button that changes
 * URL when Submit is clicked.
 */
const DateRangePicker = ({ automatic }) => {
  const { search, pathname } = useLocation()
  const history = useHistory()
  const query = new URLSearchParams(search)

  const [fromDate, setFromDate] = useState(query.get('from') ? new Date(parseInt(query.get('from'))) : null)
  const [toDate, setToDate] = useState(query.get('to') ? new Date(parseInt(query.get('to'))) : null)
  const [error, setError] = useState(false)

  const applyRange = () => {
    const today = new Date()
    if (fromDate === null) {
      setError('From date is required')
    } else if (toDate === null) {
      setError('To date is required')
    } else if (toDate > today || fromDate > today) {
      setError('Neither date can be later than today')
    } else if (fromDate > toDate) {
      setError('To date must be later than from date')
    } else {
      query.delete('status') // status xor date
      query.delete('failure')
      query.set('from', fromDate.getTime())
      query.set('to', toDate.getTime())
      history.push(`${pathname}?${query.toString()}`)
      setError(false)
    }
  }

  const updateFromDate = date => {
    setFromDate(date)
    if (automatic) {
      query.delete('status') // status xor date
      query.delete('failure')
      query.set('from', date.getTime())
      history.push(`${pathname}?${query.toString()}`)
    }
  }

  const updateToDate = date => {
    setToDate(date)
    if (automatic) {
      query.delete('status') // status xor date
      query.set('to', date.getTime())
      history.push(`${pathname}?${query.toString()}`)
    }
  }

  const unique_id = uuid()
  const to_id = 'DataRangePickerTo' + unique_id
  const from_id = 'DataRangePickerFrom' + unique_id
  return (
    <div className='lgy-funding-fee-datepicker'>
      {error && <LgyAlertDialog type='error' heading='Error' text={error} />}
      <div>
        <div>
          <label htmlFor={from_id}>
            From Date:
            <br />
            (Enter date in format mm/dd/yyyy, press enter)
          </label>
          <DatePicker id={from_id} selected={fromDate} onChange={date => updateFromDate(date)} todayButton='Today' />
        </div>
        <div>
          <label htmlFor={to_id}>
            To Date:
            <br />
            (Enter date in format mm/dd/yyyy, press enter)
          </label>
          <DatePicker
            id={to_id}
            selected={toDate}
            onChange={date => updateToDate(date)}
            maxDate={new Date()}
            todayButton='Today'
          />
        </div>
        {!automatic && <button onClick={applyRange}>Submit</button>}
      </div>
    </div>
  )
}

export default DateRangePicker
